import React, { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const SPEED_MAP = { slow: 5000, medium: 3000, fast: 2000 };

export default function SlideshowSection({ section, data, theme, settings }) {
  const images = data.images || [];
  const transition = data.transition || 'slide';  // 'slide' | 'crossfade'
  const speed = SPEED_MAP[data.speed || 'medium'];
  const autoplay = data.autoplay !== false;

  const plugins = autoplay ? [Autoplay({ delay: speed, stopOnInteraction: false, stopOnMouseEnter: true })] : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, duration: transition === 'crossfade' ? 0 : 25 },
    plugins,
  );

  const [current, setCurrent] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrent(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  if (images.length === 0) {
    return (
      <section className="py-12 text-center opacity-30" style={{ background: theme.surface || theme.bg }}>
        <p className="text-sm" style={{ color: theme.textPrimary }}>Add images to the slideshow</p>
      </section>
    );
  }

  const isCrossfade = transition === 'crossfade';

  if (isCrossfade) {
    return (
      <section
        className="relative overflow-hidden"
        style={{ background: theme.surface || theme.bg }}
        data-testid="slideshow-section"
      >
        <div className="relative w-full" style={{ aspectRatio: data.aspectRatio || '16/9' }}>
          {images.map((img, i) => (
            <img
              key={i}
              src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === current ? 1 : 0 }}
            />
          ))}
        </div>

        {/* Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                className="w-1.5 h-1.5 rounded-full transition-all"
                style={{ background: i === current ? '#fff' : 'rgba(255,255,255,0.4)' }}
                data-testid={`slideshow-dot-${i}`}
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  // Slide transition (default)
  return (
    <section
      className="relative overflow-hidden"
      style={{ background: theme.surface || theme.bg }}
      data-testid="slideshow-section"
    >
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {images.map((img, i) => (
            <div key={i} className="min-w-0 flex-[0_0_100%]">
              <img
                src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                alt=""
                className="w-full object-cover"
                style={{ aspectRatio: data.aspectRatio || '16/9' }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{ background: i === current ? '#fff' : 'rgba(255,255,255,0.4)' }}
              data-testid={`slideshow-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
